import { makeVar, useReactiveVar } from '@apollo/client';
import {
  SoundTrack,
  DEFAULT_SOUND_TRACK,
  DEFAULT_SOUND_VOLUME,
  isReadAloudTrack,
} from '@/components/toggle-sound/sound-settings';
import { localStorageService } from '@/services/local-storage-service';
import { stopReadAloud } from '@/utils/audio-utils/read-aloud.ts';

export interface StreamSoundSettings {
  track: SoundTrack;
  volume: number;
}

export type StreamSoundSettingsMap = Record<string, StreamSoundSettings>;

const STREAM_SOUND_SETTINGS_KEY = 'stream-sound-settings';

export const streamSoundSettingsVar = makeVar<StreamSoundSettingsMap>({});

const initializeStreamSoundSettings = () => {
  try {
    const stored = localStorageService.getItem(STREAM_SOUND_SETTINGS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as StreamSoundSettingsMap;
      streamSoundSettingsVar(parsed);
    }
  } catch {
    // Invalid JSON, ignore
  }
};

initializeStreamSoundSettings();

export const getStreamSoundSettings = (
  streamId: string,
): StreamSoundSettings => {
  const currentMap = streamSoundSettingsVar();

  if (!(streamId in currentMap)) {
    return {
      track: DEFAULT_SOUND_TRACK,
      volume: DEFAULT_SOUND_VOLUME,
    };
  }

  const stored = currentMap[streamId];

  return {
    track: stored.track || DEFAULT_SOUND_TRACK,
    volume: stored.volume ?? DEFAULT_SOUND_VOLUME,
  };
};

export const setStreamSoundSettings = (
  streamId: string,
  settings: StreamSoundSettings,
) => {
  const currentMap = streamSoundSettingsVar();
  const updatedMap = { ...currentMap, [streamId]: settings };

  streamSoundSettingsVar(updatedMap);
  localStorageService.setItem(
    STREAM_SOUND_SETTINGS_KEY,
    JSON.stringify(updatedMap),
  );
};

export const setStreamSoundTrack = (streamId: string, track: SoundTrack) => {
  const current = getStreamSoundSettings(streamId);
  if (isReadAloudTrack(current.track) && !isReadAloudTrack(track)) {
    stopReadAloud(streamId);
  }
  setStreamSoundSettings(streamId, { ...current, track });
};

export const setStreamSoundVolume = (streamId: string, volume: number) => {
  const current = getStreamSoundSettings(streamId);
  setStreamSoundSettings(streamId, { ...current, volume });
};

export const useStreamSoundSettings = (
  streamId: string,
): StreamSoundSettings => {
  useReactiveVar(streamSoundSettingsVar);
  return getStreamSoundSettings(streamId);
};
